import { Fragment, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useIsAdmin } from '../lib/auth';

export interface SectionTabItem {
  to: string;
  label: string;
  /** When true, NavLink only matches the exact path (used for default tabs at section root). */
  end?: boolean;
  /**
   * When set, render a non-interactive group label immediately BEFORE this
   * tab (e.g. "Monitoring", "Configuration"). Used by the Operations section
   * to visually cluster related tabs in one strip without resorting to a
   * second row of tabs.
   */
  groupStart?: string;
  /** When true, only rendered for platform admins (hidden from regular users). */
  adminOnly?: boolean;
}

interface SectionTabsProps {
  /** Accessible label for the <nav> element, e.g. "Events sections". */
  ariaLabel: string;
  items: SectionTabItem[];
  /** When true, breaks out of the parent container for data-heavy pages (Venues, Series management). */
  fluid?: boolean;
  children: ReactNode;
}

/**
 * Shared tab-strip header for top-level sections (Events, Venues, Series, Operations).
 * Renders a Bootstrap nav-tabs strip backed by react-router NavLinks; each tab is a
 * real route, so URLs are bookmarkable and the back button works as expected.
 *
 * Group labels (`item.groupStart`) render as non-interactive `<li>` separators.
 * They use `aria-hidden` because their text is decorative — screen-reader users
 * still hear each individual tab's label.
 */
export default function SectionTabs({ ariaLabel, items, fluid = false, children }: SectionTabsProps) {
  const isAdmin = useIsAdmin();
  const visible = items.filter((item) => !item.adminOnly || isAdmin);
  return (
    <div className={fluid ? 'mh-admin-fluid' : undefined}>
      <nav aria-label={ariaLabel} className="mb-4">
        <ul className="nav nav-tabs mh-section-tabs flex-nowrap align-items-center">
          {visible.map((item) => (
            <Fragment key={item.to}>
              {item.groupStart && (
                <li
                  className="mh-section-tabs__group-label small text-uppercase text-body-secondary px-2 fw-semibold"
                  aria-hidden="true"
                >
                  {item.groupStart}
                </li>
              )}
              <li className="nav-item">
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
                >
                  {item.label}
                </NavLink>
              </li>
            </Fragment>
          ))}
        </ul>
      </nav>
      {children}
    </div>
  );
}
